import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import CategoryShortcuts from './CategoryShortcuts';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Transform raw CMS page data into the homepage layout:
//   1. Keep the hero section but lock its search to location-only.
//   2. Drop all other CMS sections (old marketing content).
//   3. Inject the category-shortcuts section below the hero.
const buildHomepageData = rawData => {
  const heroSection = (rawData.sections || []).find(s => s.sectionType === 'hero');
  const sections = [
    ...(heroSection
      ? [
          {
            ...heroSection,
            // Override: location search only — no category dropdown, dates, or keyword
            callToAction: {
              fieldType: 'search',
              searchFields: {
                locationSearch: true,
                categories: false,
                dateRange: false,
                keywordSearch: false,
              },
            },
          },
        ]
      : []),
    { sectionType: 'customCategories', sectionId: 'category-shortcuts' },
  ];
  return { ...rawData, sections };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const rawData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const processedData = rawData ? buildHomepageData(rawData) : rawData;

  return (
    <PageBuilder
      pageAssetsData={processedData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      options={{
        sectionComponents: {
          customCategories: { component: CategoryShortcuts },
        },
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
